import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Package, ShoppingCart, ScanSearch, FlaskConical,
  Bell, FileBarChart, Settings, LogOut, Users, Vault, BookOpen,
  TrendingUp, Calculator, Coins, Sparkles, PackageX, MonitorSpeaker,
  UserCog, KeyRound, Store, Menu, X, CreditCard, Crown,
} from "lucide-react";

const SUPERADMIN_EMAILS = new Set(["mstfayldz357@gmail.com"]);

const groups = [
  { key: "nav.group.main", items: [
    { to: "/dashboard", icon: LayoutDashboard, key: "nav.dashboard", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-dashboard" },
    { to: "/pos", icon: ShoppingCart, key: "nav.pos", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-pos" },
    { to: "/karats", icon: Coins, key: "nav.karats", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-karats" },
  ]},
  { key: "nav.group.stock", items: [
    { to: "/products", icon: Package, key: "nav.products", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-products" },
    { to: "/stock-alerts", icon: PackageX, key: "nav.stock", roles: ["admin","kasiyer"], tid: "nav-stock" },
    { to: "/customers", icon: Users, key: "nav.customers", roles: ["admin","kasiyer"], tid: "nav-customers" },
    { to: "/vault", icon: Vault, key: "nav.vault", roles: ["admin","kasiyer"], tid: "nav-vault" },
  ]},
  { key: "nav.group.finance", items: [
    { to: "/accounts", icon: BookOpen, key: "nav.accounts", roles: ["admin","kasiyer"], tid: "nav-accounts" },
    { to: "/analytics", icon: TrendingUp, key: "nav.analytics", roles: ["admin","kasiyer"], tid: "nav-analytics" },
    { to: "/cashcount", icon: Calculator, key: "nav.cashcount", roles: ["admin","kasiyer"], tid: "nav-cashcount" },
    { to: "/z-report", icon: FileBarChart, key: "nav.zreport", roles: ["admin","kasiyer"], tid: "nav-zreport" },
  ]},
  { key: "nav.group.ai", items: [
    { to: "/vision", icon: ScanSearch, key: "nav.vision", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-vision" },
    { to: "/chat", icon: Sparkles, key: "nav.chat", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-chat" },
    { to: "/spectro", icon: FlaskConical, key: "nav.spectro", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-spectro" },
    { to: "/notify", icon: Bell, key: "nav.notify", roles: ["admin","kasiyer"], tid: "nav-notify" },
  ]},
  { key: "nav.group.system", items: [
    { to: "/signage", icon: MonitorSpeaker, key: "nav.signage", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-signage" },
    { to: "/subscription", icon: CreditCard, key: "nav.billing", roles: ["admin","kasiyer","tezgahtar"], tid: "nav-billing" },
    { to: "/users", icon: UserCog, key: "nav.users", roles: ["admin"], tid: "nav-users" },
    { to: "/settings", icon: Settings, key: "nav.settings", roles: ["admin"], tid: "nav-settings" },
  ]},
];

export default function Sidebar({ onChangePassword }) {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [shop, setShop] = useState(null);
  const roleLabel = { admin: "Admin", kasiyer: "Kasiyer", tezgahtar: "Tezgahtar" }[user?.role] || "";

  useEffect(() => {
    api.get("/tenant").then(r => setShop(r.data)).catch(() => setShop(null));
  }, [user?.tenant_id]);

  return (
    <>
      {/* Mobile toggle — daha büyük tap hedefi, altın çerçeve, safe-area destekli */}
      <button
        onClick={()=>setOpen(!open)}
        className="lg:hidden fixed z-40 h-10 w-10 flex items-center justify-center bg-[#0a0a0a]/90 backdrop-blur border transition-colors active:scale-95"
        style={{
          top: "calc(env(safe-area-inset-top) + 0.75rem)",
          left: "calc(env(safe-area-inset-left) + 0.75rem)",
          borderColor: "rgba(212,175,55,0.35)",
          color: "var(--gold)",
        }}
        data-testid="mobile-menu-toggle"
        aria-label={open ? "Menüyü kapat" : "Menüyü aç"}
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>

      {open && <div onClick={()=>setOpen(false)} className="lg:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" />}

      <aside className={`w-64 shrink-0 border-r border-white/8 bg-[#0a0a0a] flex flex-col fixed lg:sticky top-0 h-screen z-40 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} style={{ paddingTop: "env(safe-area-inset-top)", paddingLeft: "env(safe-area-inset-left)" }} data-testid="sidebar">
      <div className="px-6 py-6 border-b border-white/5">
        <div className="font-display text-2xl tracking-widest" style={{color:"var(--gold)"}}>MİLYEMSOFT</div>
        {shop && (
          <div className="mt-3 flex items-start gap-2">
            <Store size={12} className="mt-1 shrink-0" style={{color:"var(--gold)"}} />
            <div className="min-w-0">
              <div className="text-sm truncate" data-testid="shop-name">{shop.shop_name}</div>
              <div className="label-caps mt-0.5">{shop.plan?.toUpperCase()}</div>
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 py-3 overflow-y-auto" onClick={()=>setOpen(false)}>
        {SUPERADMIN_EMAILS.has((user?.email || "").toLowerCase()) && (
          <div className="mb-3">
            <div className="px-6 pt-3 pb-1 label-caps" style={{color:"var(--gold)"}}>Platform Sahibi</div>
            <NavLink
              to="/superadmin"
              data-testid="nav-superadmin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-[13px] transition-colors duration-200
                 ${isActive
                   ? "bg-white/[.03] text-white gold-border-left"
                   : "text-neutral-300 hover:text-[--gold] hover:bg-white/[.02] border-l-2 border-transparent"}`
              }
            >
              <Crown size={14} style={{color:"var(--gold)"}} />
              Süper Admin
            </NavLink>
          </div>
        )}
        {groups.map(g => {
          const visible = g.items.filter(i => i.roles.includes(user?.role));
          if (visible.length === 0) return null;
          return (
            <div key={g.key} className="mb-3">
              <div className="px-6 pt-3 pb-1 label-caps text-neutral-600">{t(g.key)}</div>
              {visible.map(it => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/dashboard"}
                  data-testid={it.tid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-2.5 text-[13px] transition-colors duration-200
                     ${isActive
                       ? "bg-white/[.03] text-white gold-border-left"
                       : "text-neutral-400 hover:text-white hover:bg-white/[.02] border-l-2 border-transparent"}`
                  }
                >
                  <it.icon size={14} />
                  {t(it.key)}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/5 p-4 space-y-3 relative overflow-visible">
        <LanguageSwitcher compact={false} direction="up" />
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 border flex items-center justify-center font-display text-sm" style={{borderColor:"var(--gold)", color:"var(--gold)"}}>
            {user?.name?.[0] || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{user?.name}</div>
            <div className="label-caps">{roleLabel}</div>
          </div>
          <button onClick={onChangePassword} data-testid="nav-change-password" className="p-2 text-neutral-400 hover:text-[--gold]" title={t("nav.change_password")}><KeyRound size={14} /></button>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn" className="p-2 text-neutral-400 hover:text-white" title={t("nav.logout")}><LogOut size={16} /></button>
        </div>
      </div>
    </aside>
    </>
  );
}
